import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Building2, 
  Edit, 
  ShoppingCart, 
  FileText, 
  Package, 
  Calendar,
  User,
  MapPin,
  Truck,
  CheckCircle,
  Clock,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { chantierService } from '../../services/chantierService';
import { retourService } from '../../services/retourService';
import { productService } from '../../services/productService';
import { Chantier, ChantierOrder, ChantierQuote, RetourChantier } from '../../types';

function ChantierDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [chantier, setChantier] = useState<Chantier | null>(null);
  const [orders, setOrders] = useState<ChantierOrder[]>([]);
  const [quotes, setQuotes] = useState<ChantierQuote[]>([]);
  const [retours, setRetours] = useState<RetourChantier[]>([]);
  const [retoursSummary, setRetoursSummary] = useState({
    totalRetours: 0,
    totalValue: 0,
    validatedRetours: 0,
    validatedValue: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadChantierData();
    }
  }, [id]);

  const loadChantierData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [chantierData, ordersData, quotesData, retoursData, retoursSummaryData] = await Promise.all([
        chantierService.getChantier(id),
        chantierService.getChantierOrders(id),
        chantierService.getChantierQuotes(id),
        retourService.getRetoursByChantier(id),
        retourService.getChantierRetoursSummary(id)
      ]);
      
      setChantier(chantierData);
      setOrders(ordersData);
      setQuotes(quotesData);
      setRetours(retoursData);
      setRetoursSummary(retoursSummaryData);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les détails du chantier',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = () => {
    // Rediriger vers la création de commande avec le chantier pré-sélectionné
    addNotification({
      userId: user?.id || '',
      title: 'Commande créée',
      message: 'Nouvelle commande chantier créée',
      type: 'success'
    });
  };

  const canManageChantier = user && chantier && (
    user.role === 'admin' || 
    user.role === 'responsable_depot' || 
    user.role === 'responsable_bureau' ||
    (user.role === 'responsable_equipe' && chantier.chefEquipeId === user.id)
  );

  const canCreateOrder = user && chantier && (
    chantierService.canUserAccessChantier(user.id, user.role, chantier)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!chantier) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Chantier non trouvé</h3>
        <Link to="/chantiers" className="mt-2 text-blue-600 hover:text-blue-800">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg shadow-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{chantier.name}</h1>
                <p className="text-gray-600">Code: {chantier.code}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {canCreateOrder && (
                <button
                  onClick={handleCreateOrder}
                  className="btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span>Créer commande</span>
                </button>
              )}
              
              {canManageChantier && (
                <Link
                  to={`/chantiers/${chantier.id}/edit`}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit className="h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chantier Info */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations chantier</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    chantierService.getStatusColor(chantier.status)
                  }`}>
                    {chantierService.getStatusLabel(chantier.status)}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chef d'équipe
                  </label>
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {chantier.chefEquipeId === '3' ? 'Thomas A.' : 'Luc T.'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de début
                  </label>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {new Date(chantier.startDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                
                {chantier.endDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date de fin
                    </label>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {new Date(chantier.endDate).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {chantier.address && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{chantier.address}</span>
                  </div>
                </div>
              )}
              
              {chantier.description && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <p className="text-sm text-gray-900">{chantier.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Orders */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Commandes ({orders.length})
              </h3>
            </div>
            
            {orders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucune commande</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <div key={order.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          order.status === 'panier' ? 'bg-blue-100 text-blue-600' :
                          order.status === 'a_preparer' ? 'bg-orange-100 text-orange-600' :
                          order.status === 'prete' ? 'bg-green-100 text-green-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {order.status === 'panier' ? <ShoppingCart className="h-4 w-4" /> :
                           order.status === 'a_preparer' ? <Clock className="h-4 w-4" /> :
                           order.status === 'prete' ? <Package className="h-4 w-4" /> :
                           <CheckCircle className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Commande #{order.id.slice(-6)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.createdAt).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          chantierService.getStatusColor(order.status)
                        }`}>
                          {chantierService.getStatusLabel(order.status)}
                        </span>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {order.totalAmount.toFixed(2)} €
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-sm text-gray-600">
                      {order.items.length} article{order.items.length > 1 ? 's' : ''}
                      {order.notes && (
                        <span className="ml-2 text-gray-500">• {order.notes}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quotes */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Devis ({quotes.length})
              </h3>
            </div>
            
            {quotes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucun devis</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {quotes.map((quote) => (
                  <div key={quote.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Devis #{quote.id.slice(-6)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(quote.createdAt).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          chantierService.getStatusColor(quote.status)
                        }`}>
                          {chantierService.getStatusLabel(quote.status)}
                        </span>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {quote.totalAmount.toFixed(2)} €
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Retours */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Retours de chantier ({retours.length})
                </h3>
                {retoursSummary.validatedValue > 0 && (
                  <span className="text-sm font-medium text-green-600">
                    -{retoursSummary.validatedValue.toFixed(2)} € récupérés
                  </span>
                )}
              </div>
            </div>
            
            {retours.length === 0 ? (
              <div className="text-center py-8">
                <RotateCcw className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucun retour</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {retours.map((retour) => (
                  <div key={retour.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <RotateCcw className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Retour #{retour.retourNumber}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(retour.createdAt).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          retourService.getStatusColor(retour.status)
                        }`}>
                          {retourService.getStatusLabel(retour.status)}
                        </span>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {retour.totalValue.toFixed(2)} €
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-sm text-gray-600">
                      {retour.items.length} article{retour.items.length > 1 ? 's' : ''}
                      {retour.reason && (
                        <span className="ml-2 text-gray-500">• {retour.reason}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Actions rapides</h3>
            </div>
            
            <div className="p-6 space-y-3">
              {canCreateOrder && (
                <button
                  onClick={handleCreateOrder}
                  className="w-full btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span>Créer commande</span>
                </button>
              )}
              
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Créer devis</span>
              </button>
              
              <button className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <Truck className="h-4 w-4" />
                <span>Bon de livraison</span>
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Statistiques</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Commandes totales</span>
                <span className="text-sm font-medium text-gray-900">{orders.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Devis totaux</span>
                <span className="text-sm font-medium text-gray-900">{quotes.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Retours</span>
                <span className="text-sm font-medium text-gray-900">{retours.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Montant total</span>
                <span className="text-sm font-medium text-gray-900">
                  {(orders.reduce((sum, o) => sum + o.totalAmount, 0) + 
                    quotes.reduce((sum, q) => sum + q.totalAmount, 0) - 
                    retoursSummary.validatedValue).toFixed(2)} €
                </span>
              </div>
              
              {retoursSummary.validatedValue > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="text-sm">Économies retours</span>
                  <span className="text-sm font-medium">
                    -{retoursSummary.validatedValue.toFixed(2)} €
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChantierDetail;